/* BookFollowUpBooking — DocSite Turbo: real follow-up booking inside the
   patient Digital Health File. Fetches live availability, lets the patient
   pick a date + slot, and books with the DHF attached so the doctor reviews
   it before the visit. Self-contained (auth + navigation handled here). */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, MapPin, CheckCircle2, Loader2, Lock, LogIn, FileCheck2 } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildDays(n = 14) {
  const out = [];
  const t = new Date();
  for (let i = 0; i < n; i++) { const d = new Date(t); d.setDate(t.getDate() + i); out.push(d); }
  return out;
}

export default function BookFollowUpBooking({ slug, healthFileToken, doctorName, consent, disabled }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [date, setDate] = useState(new Date());
  const [avail, setAvail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState(null);          // { location_id, location_name, slot }
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const days = buildDays(14);
  const inactive = disabled || !slug;

  useEffect(() => {
    if (inactive) return;
    let live = true;
    setLoading(true); setPick(null);
    api.get(`/doctor/${slug}/availability`, { params: { date: fmt(date) } })
      .then(r => { if (live) setAvail(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setAvail([]); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [slug, date, inactive]);

  if (!slug) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-500" data-testid="phf-booking-preview">
        Live booking appears here on your real health file — pick a date, choose a slot, and your file is attached for the doctor.
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-800" data-testid="phf-booking-newdoctor">
        Booking with a different doctor arrives with the DocSite doctor network. For now, choose <b>your current doctor</b> above to book your follow-up.
      </div>
    );
  }

  if (result) {
    return (
      <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="phf-booking-success">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 size={18} className="text-emerald-600" />
          <p className="text-sm font-bold text-slate-900">Follow-up booked</p>
        </div>
        <p className="text-[13px] text-slate-700"><b>{date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</b> · {result.start_time}–{result.end_time}</p>
        <p className="text-[12px] text-slate-500 mt-0.5">{result.location_name}</p>
        {consent && healthFileToken && (
          <p className="text-[12px] text-emerald-700 mt-2 flex items-center gap-1.5"><FileCheck2 size={14} /> Your health file is attached for {doctorName || 'the doctor'} to review before the visit.</p>
        )}
        <button onClick={() => { setResult(null); setPick(null); }} className="mt-3 text-[12px] font-semibold text-sky-700 hover:underline" data-testid="phf-book-another">Book another time</button>
      </div>
    );
  }

  const confirm = async () => {
    if (!pick) return;
    if (!user) { navigate(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    setBooking(true); setError('');
    try {
      const { data } = await api.post('/bookings', {
        doctor_slug: slug,
        location_id: pick.location_id,
        date: fmt(date),
        start_time: pick.slot.start_time,
        end_time: pick.slot.end_time,
        booking_type: 'opd',
        notes: 'Follow-up booked from Digital Health File',
        health_file_token: consent ? healthFileToken : undefined,
      });
      setResult({ ...data, location_name: pick.location_name });
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Could not complete booking');
    } finally { setBooking(false); }
  };

  return (
    <div data-testid="phf-booking">
      {/* Date strip */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5" data-testid="phf-booking-dates">
        {days.map((d) => {
          const on = fmt(d) === fmt(date);
          return (
            <button key={fmt(d)} onClick={() => setDate(d)} type="button"
              className={`shrink-0 w-12 py-1.5 rounded-lg border text-center transition-colors ${on ? 'bg-[#16335b] border-[#16335b] text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
              data-testid={`phf-date-${fmt(d)}`}>
              <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">{DOW[d.getDay()]}</div>
              <div className="text-sm font-bold leading-none mt-0.5">{d.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Slots */}
      <div className="mt-3 min-h-[44px]">
        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-400 py-2"><Loader2 size={14} className="animate-spin" /> Loading slots…</div>
        ) : avail.length === 0 ? (
          <p className="text-[12px] text-slate-400 py-2" data-testid="phf-no-slots">No slots on this day — try another date.</p>
        ) : (
          avail.map((loc) => {
            const open = (loc.slots || []).filter(s => s.is_available);
            return (
              <div key={loc.location_id} className="mb-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 mb-1.5"><MapPin size={12} /> {loc.location_name}</div>
                {open.length === 0 ? (
                  <p className="text-[11px] text-slate-400">No open slots here.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {open.slice(0, 18).map((s) => {
                      const sel = pick && pick.location_id === loc.location_id && pick.slot.start_time === s.start_time;
                      return (
                        <button key={s.start_time} type="button"
                          onClick={() => setPick({ location_id: loc.location_id, location_name: loc.location_name, slot: s })}
                          className={`px-2.5 py-1 rounded-md border text-[12px] font-medium transition-colors ${sel ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-emerald-300'}`}
                          data-testid={`phf-slot-${s.start_time}`}>
                          {s.start_time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {!consent && (
        <p className="text-[11px] text-amber-700 flex items-center gap-1.5 mb-2"><Lock size={12} /> Turn on consent above to attach your health file to this booking.</p>
      )}
      {error && <p className="text-[12px] text-rose-600 mb-2" data-testid="phf-booking-error">{error}</p>}

      <button onClick={confirm} disabled={!pick || booking}
        className="w-full mt-1 py-2.5 rounded-full bg-[#16335b] text-white text-sm font-bold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="phf-confirm-booking">
        {booking ? <Loader2 size={15} className="animate-spin" /> : (!user ? <LogIn size={15} /> : <Calendar size={15} />)}
        {booking ? 'Booking…' : !user ? 'Log in to book' : pick ? `Confirm ${pick.slot.start_time} follow-up` : 'Pick a slot to confirm'}
      </button>
    </div>
  );
}
